import {
  createRootRouteWithContext,
  Outlet,
  HeadContent,
  Scripts,
  Link,
  useSearch,
  useRouter,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { closeNoteModal, closeEntityModal } from "@/utils/noteNavigation";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "sonner";
import type { Theme } from "@/store/theme";
import { getThemeFromRequest } from "@/lib/theme.server";
import { AppSidebar } from "@/components/ui/app-sidebar";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { getCurrentUserServer } from "@/server/auth";
import { getNotes } from "@/server/notes";
import { notesQueryOptions } from "@/queries/notes";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/lib/http";
import appCss from "@/app.css?url";
import { useDocumentTitle } from "usehooks-ts";

const NoteDetailModal = lazy(() =>
  import("@/components/note-detail-modal").then((mod) => ({
    default: mod.NoteDetailModal,
  })),
);
const EntityDetailModal = lazy(() =>
  import("@/components/EntityDetailModal").then((mod) => ({
    default: mod.EntityDetailModal,
  })),
);

/**
 * Root route context type
 * Shared across all routes in the application
 */
interface RootRouteContext {
  queryClient: QueryClient;
  user: {
    id: string;
    email: string;
    name: string | null;
    profilePicture: string | null;
  } | null;
  theme: Theme;
}

/**
 * Root route with context typing
 * - Provides QueryClient for React Query
 * - Provides user session data
 * - Provides theme for SSR consistency
 */
interface RootSearch {
  note?: string;
  entity?: string;
  createNote?: string;
}

export const Route = createRootRouteWithContext<RootRouteContext>()({
  validateSearch: (search: Record<string, unknown>): RootSearch => ({
    note: typeof search.note === "string" ? search.note : undefined,
    entity: typeof search.entity === "string" ? search.entity : undefined,
    createNote:
      typeof search.createNote === "string" ? search.createNote : undefined,
  }),

  beforeLoad: async (opts) => {
    const { context } = opts;

    // Get theme from cookies for SSR consistency
    const theme = await getThemeFromRequest();

    // Fetch authenticated user using session cookie
    let user = null;
    try {
      const userData = await getCurrentUserServer();
      if (userData) {
        user = {
          id: userData.id,
          email: userData.email,
          name: userData.name ?? null,
          profilePicture: userData.profilePicture ?? null,
        };
      }
    } catch (error) {
      if (!ApiError.isUnauthorized(error)) {
        console.error("[Root beforeLoad] Error fetching user:", error);
      }
      user = null;
    }

    // Fetch notes and seed React Query cache for all child routes
    if (user) {
      const notes = await getNotes();
      // Seed cache - sidebar and home route will read from here
      context.queryClient.setQueryData(["notes", user.id], notes);

      // Pre-seed type-specific caches for SSR - this makes collection pages instant!
      const noteTypes = ["todo", "watch", "link", "bookmark"];
      noteTypes.forEach((type) => {
        const filtered = notes.filter((note: any) => note.type === type);
        context.queryClient.setQueryData(["notes", "type", type], filtered);
      });
    }

    return {
      theme,
      user,
    };
  },

  component: RootComponent,

  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold">404</h1>
        <p className="text-muted-foreground">Page not found</p>
        <Link to="/" className="text-primary hover:underline">
          Go home
        </Link>
      </div>
    </div>
  ),

  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=optional",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png",
      },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "icon", href: "/favicon.ico" },
    ],
  }),
});

/**
 * Root layout component
 * Sets up HTML structure and applies theme class
 */
function RootComponent() {
  const context = Route.useRouteContext();
  const theme = context.theme || "light";
  const router = useRouter();
  const search = useSearch({ from: "__root__" });
  const noteId = search.note;
  const entityId = search.entity;

  // Read notes from cache (seeded in beforeLoad)
  const { data: notes = [] } = useQuery({
    ...notesQueryOptions(context.user?.id),
    enabled: !!context.user?.id,
  });

  useDocumentTitle("Notebook");

  return (
    <QueryClientProvider client={context.queryClient}>
      <html lang="en" className={theme === "dark" ? "dark" : ""}>
        <head>
          <HeadContent />
        </head>
        <body>
          <SidebarProvider>
            <AppSidebar user={context.user} notes={notes} />
            <SidebarInset className="gap-4">
              <header className="flex h-14 items-center gap-2 px-4 md:hidden">
                <SidebarTrigger />
                <span className="font-semibold font-display text-lg">
                  Notebook
                </span>
              </header>
              <Outlet />
            </SidebarInset>
            <Suspense fallback={null}>
              {noteId && (
                <NoteDetailModal
                  noteId={noteId}
                  onClose={() => closeNoteModal(router)}
                />
              )}
              {entityId && (
                <EntityDetailModal
                  entityId={entityId}
                  onClose={() => closeEntityModal(router)}
                />
              )}
            </Suspense>
            <ReactQueryDevtools initialIsOpen={false} />
            <Toaster
              position="bottom-right"
              theme={theme}
              richColors
              closeButton
            />
          </SidebarProvider>
          <Scripts />
        </body>
      </html>
    </QueryClientProvider>
  );
}
